import { Button } from "@/components/ui/button";
import { Sparkles, RefreshCw, MessageCircleQuestion, Pill, FlaskConical, History } from "lucide-react";
import { formatDate } from "@/lib/date";

// The AI-generated "Patient Brief" panel shown at the top of a consultation —
// overview, main concern, current medicines, recent lab changes and previous
// visit summary. Data/state (brief, loading, regenerate handler) owned by parent.
export default function ConsultationBriefPanel({ brief, briefLoading, regenerateBrief }) {
  return (
    <div className="card-surface card-surface-hover rounded-xl border border-teal-200 bg-teal-50/40 shadow-sm p-6" data-testid="consultation-brief-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-semibold text-slate-900">
            <Sparkles className="h-4 w-4 text-teal-700" /> Patient Brief
          </h2>
          {brief && <p className="mt-1 text-xs text-slate-500">Last generated {formatDate(brief.generated_at)}</p>}
        </div>
        <Button
          data-testid="regenerate-brief-btn"
          size="sm"
          variant="outline"
          disabled={briefLoading}
          onClick={regenerateBrief}
          className="gap-2 border-teal-700 text-teal-700"
        >
          <RefreshCw className={`h-3 w-3 ${briefLoading ? "animate-spin" : ""}`} /> {briefLoading ? "Generating..." : "Regenerate"}
        </Button>
      </div>

      {briefLoading && !brief && <p className="mt-4 text-sm text-slate-500">Generating pre-consultation brief...</p>}

      {brief && (
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="consultation-brief-results">
          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <p className="text-xs font-semibold uppercase text-slate-500">Patient Overview</p>
            <p className="mt-1 text-sm text-slate-800">Age: {brief.age || "—"} · Diagnosis Duration: {brief.diagnosis_duration}</p>
            {(brief.known_conditions || brief.allergies) && (
              <p className="mt-1 text-xs text-slate-500">
                {brief.known_conditions && <>Known Conditions: {brief.known_conditions}</>}
                {brief.known_conditions && brief.allergies && " · "}
                {brief.allergies && <>Allergies: {brief.allergies}</>}
              </p>
            )}
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="brief-main-concern">
            <p className="flex items-center gap-1 text-xs font-semibold uppercase text-slate-500">
              <MessageCircleQuestion className="h-3 w-3" /> Main Concern (Today's Visit)
            </p>
            <p className="mt-1 text-sm text-slate-800">{brief.main_concern}</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="brief-current-medicines">
            <p className="flex items-center gap-1 text-xs font-semibold uppercase text-slate-500">
              <Pill className="h-3 w-3" /> Current Medicines
            </p>
            {brief.current_medicines?.length ? (
              <>
                <ul className="mt-1 list-inside list-disc text-sm text-slate-800">
                  {brief.current_medicines.map((m, i) => <li key={`${i}-${m}`}>{m}</li>)}
                </ul>
                {brief.medicines_source && <p className="mt-1 text-xs italic text-slate-400">{brief.medicines_source}</p>}
              </>
            ) : (
              <p className="mt-1 text-sm text-slate-400">None recorded.</p>
            )}
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="brief-lab-changes">
            <p className="flex items-center gap-1 text-xs font-semibold uppercase text-slate-500">
              <FlaskConical className="h-3 w-3" /> Recent Laboratory Changes
            </p>
            {brief.recent_lab_changes?.length ? (
              <ul className="mt-1 list-inside list-disc text-sm text-slate-800">
                {brief.recent_lab_changes.map((m, i) => <li key={`${i}-${m}`}>{m}</li>)}
              </ul>
            ) : (
              <p className="mt-1 text-sm text-slate-400">No comparable lab trend data available yet.</p>
            )}
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-3 sm:col-span-2" data-testid="brief-previous-visit">
            <p className="flex items-center gap-1 text-xs font-semibold uppercase text-slate-500">
              <History className="h-3 w-3" /> Previous Visit Summary
            </p>
            {brief.previous_visit_summary?.length ? (
              <ul className="mt-1 list-inside list-disc text-sm text-slate-800">
                {brief.previous_visit_summary.map((m, i) => <li key={`${i}-${m}`}>{m}</li>)}
              </ul>
            ) : (
              <p className="mt-1 text-sm text-slate-400">No prior visit history on file.</p>
            )}
          </div>
          {brief.data_limitations_note && (
            <p className="text-xs italic text-slate-400 sm:col-span-2">{brief.data_limitations_note}</p>
          )}
        </div>
      )}
    </div>
  );
}
